/**
 * FeedbackMetadata - Molecule component for displaying feedback metadata
 */
import React from 'react';
import JsonViewer from '../atoms/JsonViewer';
import UserAvatar from '../atoms/UserAvatar';
import { formatDistanceToNow, format } from 'date-fns';

interface UserSummary {
  auth0_user_id: string;
  name?: string;
  email?: string;
  picture?: string;
}

interface AppSummary {
  client_id: string;
  name: string;
  description?: string;
  logo_uri?: string;
}

interface FeedbackMetadataProps {
  feedback: {
    id: number;
    created_at: string;
    updated_at?: string;
    resolved_at?: string;
    user?: UserSummary;
    app?: AppSummary;
    assigned_user?: UserSummary;
    resolver?: UserSummary;
    external_id?: string;
    source_url?: string;
    extra_data?: any;
  };
  className?: string;
}

const FeedbackMetadata: React.FC<FeedbackMetadataProps> = ({
  feedback,
  className = ''
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      absolute: format(date, 'PPpp'), // Nov 12, 2024 at 2:30 PM
      relative: formatDistanceToNow(date, { addSuffix: true })
    };
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Timing Information */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Timeline
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">Created</span>
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatDate(feedback.created_at).relative}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(feedback.created_at).absolute}
              </div>
            </div>
          </div>
          
          {feedback.updated_at && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Updated</span>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatDate(feedback.updated_at).relative}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(feedback.updated_at).absolute}
                </div>
              </div>
            </div>
          )}
          
          {feedback.resolved_at && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Resolved</span>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatDate(feedback.resolved_at).relative}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(feedback.resolved_at).absolute}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Information */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          User Information
        </h3>
        {feedback.user ? (
          <div className="flex items-center space-x-3">
            <UserAvatar
              name={feedback.user.name}
              email={feedback.user.email}
              picture={feedback.user.picture}
              size="md"
            />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {feedback.user.name || 'No name provided'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {feedback.user.email || 'No email provided'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                ID: {feedback.user.auth0_user_id}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            No user information available
          </div>
        )}
      </div>

      {/* App Information */}
      {feedback.app && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Application
          </h3>
          <div className="flex items-center space-x-3">
            {feedback.app.logo_uri ? (
              <img
                src={feedback.app.logo_uri}
                alt={`${feedback.app.name} logo`}
                className="w-10 h-10 rounded-lg object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-400 text-lg">📱</span>
              </div>
            )}
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {feedback.app.name}
              </div>
              {feedback.app.description && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {feedback.app.description}
                </div>
              )}
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Client ID: {feedback.app.client_id}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Information */}
      {feedback.assigned_user && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Assigned To
          </h3>
          <div className="flex items-center space-x-3">
            <UserAvatar
              name={feedback.assigned_user.name}
              email={feedback.assigned_user.email}
              picture={feedback.assigned_user.picture}
              size="md"
            />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {feedback.assigned_user.name || 'No name provided'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {feedback.assigned_user.email}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resolution Information */}
      {feedback.resolver && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Resolved By
          </h3>
          <div className="flex items-center space-x-3">
            <UserAvatar
              name={feedback.resolver.name}
              email={feedback.resolver.email}
              picture={feedback.resolver.picture}
              size="md"
            />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {feedback.resolver.name || 'No name provided'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {feedback.resolver.email}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* External References */}
      {(feedback.external_id || feedback.source_url) && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            External References
          </h3>
          <div className="space-y-2">
            {feedback.external_id && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">External ID</span>
                <code className="text-sm font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {feedback.external_id}
                </code>
              </div>
            )}
            {feedback.source_url && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Source URL</span>
                <a
                  href={feedback.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate max-w-xs"
                >
                  {feedback.source_url}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional Data */}
      {feedback.extra_data && (
        <JsonViewer
          data={feedback.extra_data}
          title="Additional Data"
          collapsed={true}
          className="mt-4"
        />
      )}
    </div>
  );
};

export default FeedbackMetadata; 